import { Button, Form, Input, Select, Spin } from 'antd'
import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import routes from '@/constants/routes'
import { Detergent, Tank, TankMethodType } from '@/types'
import { UpdateTankRequest } from '@/types/apis/tanks/updateTankRequest'

import { SelectWrapper } from '../../Customer/styles'
import { SpaceCustom } from '../../style'

type Props = {
  updateLoading: boolean
  getTankResponse: Tank | undefined
  getLoading: boolean
  detergents: Detergent[] | undefined
  onSubmit: (formValues: UpdateTankRequest) => void
}

type Options = {
  value: TankMethodType
  label: string
}

const UpdateTankForm = ({ updateLoading, getLoading, getTankResponse, detergents, onSubmit }: Props) => {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const [form] = Form.useForm<UpdateTankRequest>()
  const [netWeightMl, setNetWeightMl] = useState(0)

  const tankOptions: Options[] = [
    { value: TankMethodType.MANUAL, label: t('components.pages.tanks.update.form.manual') },
    { value: TankMethodType.AUTOMATIC, label: t('components.pages.tanks.update.form.automatic') },
  ]

  useEffect(() => {
    if (getTankResponse) {
      const detergent = getTankResponse.detergents[0]
      form.setFieldsValue({ ...getTankResponse, detergentIds: detergent ? [detergent.id] : [] })
      if (detergent) {
        setNetWeightMl(detergent.netWeightMl)
      }
    }
  }, [getTankResponse, form])

  const handleSelectDetergent = (value: number) => {
    const detergent = detergents?.find((d) => d.id === value)
    if (detergent) {
      setNetWeightMl(detergent.netWeightMl)
    }
  }

  return (
    <Spin spinning={getLoading}>
      <Form layout="vertical" onFinish={onSubmit} form={form}>
        <Form.Item
          label={t('components.pages.tanks.update.form.tankNo')}
          name="tankNo"
          rules={[{ required: true, message: t('components.pages.tanks.update.form.requireTankNo') }]}
        >
          <Input type="number" min={0} disabled />
        </Form.Item>

        <SelectWrapper>
          <Form.Item
            label={t('components.pages.tanks.update.form.detergentName')}
            name="detergentIds"
            rules={[{ required: true, message: t('components.pages.tanks.update.form.requireDetergentName') }]}
          >
            <Select onChange={handleSelectDetergent}>
              {detergents?.map((detergent) => (
                <Select.Option key={detergent.id} value={detergent.id}>
                  {detergent.name}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </SelectWrapper>

        <SelectWrapper>
          <Form.Item
            label={t('components.pages.tanks.update.form.method')}
            name="method"
            rules={[{ required: true, message: t('components.pages.tanks.update.form.requireMethod') }]}
          >
            <Select allowClear>
              {tankOptions.map((option, index) => (
                <Select.Option key={index} value={option.value}>
                  {option.label}
                </Select.Option>
              ))}
            </Select>
          </Form.Item>
        </SelectWrapper>

        <Form.Item label={t('components.pages.tanks.update.form.netWeightMl')}>
          <Input type="number" min={0} suffix="ml" disabled value={netWeightMl} />
        </Form.Item>

        <Form.Item
          label={t('components.pages.tanks.update.form.thresholdQuantity')}
          name="thresholdQuantity"
          rules={[{ required: true, message: t('components.pages.tanks.update.form.requireThresholdQuantity') }]}
        >
          <Input type="number" min={0} suffix="ml" />
        </Form.Item>

        <Form.Item
          label={t('components.pages.tanks.update.form.remainingQuantity')}
          name="remainingQuantity"
          rules={[{ required: true, message: t('components.pages.tanks.update.form.requireRemainingQuantity') }]}
        >
          <Input type="number" min={0} suffix="ml" />
        </Form.Item>

        <Form.Item>
          <SpaceCustom>
            <Button
              onClick={() => {
                navigate(routes.tanks.path)
              }}
            >
              {t('components.pages.tanks.update.form.cancel')}
            </Button>
            <Button type="primary" htmlType="submit" loading={updateLoading}>
              {t('components.pages.tanks.update.form.update')}
            </Button>
          </SpaceCustom>
        </Form.Item>
      </Form>
    </Spin>
  )
}

UpdateTankForm.displayName = 'UpdateTankForm'
export default UpdateTankForm
